import { redirect, type ShouldRevalidateFunctionArgs } from 'react-router';
import { ApiError } from '@/api/user-portal-api-generated';
import {
  SessionService,
  UserService,
  updateApiBaseUrl,
} from '@/api/user-portal-service';
import { isPublicPath, ROUTES } from '@/common/auth-routes';
import {
  getPostAuthRedirect,
  initiateSamlAuth,
  isTenantNotFoundError,
  storePostAuthRedirect,
} from '@/common/auth-utils';
import {
  buildTenantPath,
  getTenantFromPathString,
} from '@/common/tenant-utils';
import { logError } from '@/utils/error-logger';

export interface AuthLoaderData {
  authenticated: boolean;
  user?: {
    id: string;
    username: string;
    email: string;
    name: string;
    first_name?: string | null;
    last_name?: string | null;
    secondary_email?: string | null;
    phone_number?: string | null;
  };
}

async function initiateSamlOrFallback(
  tenantSlug: string,
  postAuthRedirect: string
): Promise<Response> {
  storePostAuthRedirect(postAuthRedirect);

  try {
    await initiateSamlAuth();

    // Browser is navigating away via window.location.replace.
    // Return a never-resolving promise so React Router stays in
    // loading state — prevents flash of dashboard/route content.
    return new Promise<never>(() => {
      /* browser navigating away */
    });
  } catch (error) {
    logError(error, 'auth:initiateSamlAuth');

    if (isTenantNotFoundError(error)) {
      return redirect(ROUTES.TENANT_SELECTION);
    }

    return redirect(buildTenantPath(tenantSlug, ROUTES.AUTH_ERROR));
  }
}

/**
 * Authentication loader that runs before route rendering
 * This prevents any component from rendering until auth is checked
 *
 * Since the backend validates the cookie on every API call,
 * we only need to check authentication when:
 * - Initially loading the app
 * - Switching tenants
 * - After login/logout actions
 */
export async function authLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const pathname = url.pathname;
  const pathWithQuery = pathname + url.search;
  const tenantSlug = getTenantFromPathString(pathname);

  // Skip auth check for public routes
  if (isPublicPath(pathname)) {
    return null;
  }

  // Update API base URL with tenant from request URL
  // This is necessary because window.location hasn't been updated yet
  // during React Router's loader phase
  updateApiBaseUrl(tenantSlug ?? undefined);

  try {
    // Check authentication status
    const isAuthenticated = await SessionService.authenticated();

    if (!isAuthenticated && tenantSlug) {
      const isTenantRoot =
        pathname === `/${tenantSlug}` || pathname === `/${tenantSlug}/`;
      // Tenant root redirects post-auth to the dashboard since `/` carries
      // no destination; deeper paths land back on the original URL.
      const postAuthRedirect = isTenantRoot
        ? buildTenantPath(tenantSlug, ROUTES.DASHBOARD)
        : pathWithQuery;

      return initiateSamlOrFallback(tenantSlug, postAuthRedirect);
    }

    if (!isAuthenticated && !tenantSlug) {
      return redirect(ROUTES.TENANT_SELECTION);
    }

    // Authenticated - check for post-auth redirect
    const redirectPath = getPostAuthRedirect();

    if (redirectPath && redirectPath !== pathWithQuery) {
      return redirect(redirectPath);
    }

    // Fetch user profile - handle errors gracefully to avoid infinite redirect loops
    let user: AuthLoaderData['user'];

    try {
      const userProfile = await UserService.getCurrentUserProfile();
      user = {
        id: userProfile.id,
        username: userProfile.username,
        email: userProfile.email,
        name: userProfile.full_name,
        first_name: userProfile.first_name,
        last_name: userProfile.last_name,
        secondary_email: userProfile.secondary_email,
        phone_number: userProfile.phone_number,
      };
    } catch (profileError: unknown) {
      if (
        profileError instanceof ApiError &&
        (profileError.status === 401 || profileError.status === 403)
      ) {
        logError(profileError, 'auth:profileFetch:authError');

        if (tenantSlug) {
          return initiateSamlOrFallback(tenantSlug, pathWithQuery);
        }

        return redirect(ROUTES.TENANT_SELECTION);
      }

      // For transient errors (500, network), continue without profile
      // Components should handle user being undefined gracefully
      logError(profileError, 'auth:profileFetch:transient');
    }

    return {
      authenticated: true,
      user,
    } satisfies AuthLoaderData;
  } catch (error) {
    logError(error, 'auth:authLoader');

    if (isTenantNotFoundError(error) || !tenantSlug) {
      return redirect(ROUTES.TENANT_SELECTION);
    }

    return initiateSamlOrFallback(tenantSlug, pathWithQuery);
  }
}

/**
 * shouldRevalidate function to control when loaders re-run
 * Only check auth when switching to a different tenant
 */
export function authShouldRevalidate({
  currentUrl,
  nextUrl,
}: ShouldRevalidateFunctionArgs): boolean {
  const currentTenant = getTenantFromPathString(currentUrl.pathname);
  const nextTenant = getTenantFromPathString(nextUrl.pathname);

  // Don't revalidate when navigating to public paths
  if (isPublicPath(nextUrl.pathname)) {
    return false;
  }

  // Only revalidate if navigating to a different tenant
  return currentTenant !== nextTenant;
}
